/**
 * Выпадающий выбор с поиском — вместо `<select>` везде (разметка владельца
 * 30.09.26 на «Операциях»: «всегда все комбо-боксы должны быть поиском»).
 *
 * Кнопка выглядит как поле; открытый список сразу ловит ввод: ищет по
 * названию и по группе (группа нашлась — видна целиком). Стрелки, Enter, Esc.
 * Список рисуется в `body` с фиксированной позицией: в строке таблицы с
 * `overflow-x-auto` абсолютный попап обрезался бы.
 */
import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ComboOption = {
  /** Значение как строка; `""` — законный пункт («все», «не выбрано»). */
  value: string;
  label: string;
  /** Заголовок группы; подряд идущие пункты одной группы — под ним. */
  group?: string;
};

const FIELD =
  "flex items-center gap-1.5 rounded-[var(--radius-ui)] border bg-bg text-left outline-none hover:border-border-strong focus-visible:border-border-strong";
const SIZE = { md: "h-8 px-2 text-[12.5px]", sm: "h-7 px-1.5 text-[12px]" };

export function Combobox(props: {
  value: string;
  options: ComboOption[];
  onChange: (value: string) => void;
  /** Подпись, когда значения нет среди пунктов. */
  placeholder?: string;
  size?: "md" | "sm";
  /** Ширина кнопки, например `w-full`. */
  className?: string;
  /** Жёлтая рамка: значение требует внимания («не разнесено»). */
  warn?: boolean;
  title?: string;
}) {
  const { options, value } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<{ left: number; top: number; width: number; up: boolean } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const current = options.find((o) => o.value === value);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.group?.toLowerCase().includes(q));
  }, [options, query]);

  const close = (focus = true) => {
    setOpen(false);
    setQuery("");
    if (focus) button.current?.focus();
  };

  const pick = (option: ComboOption) => {
    if (option.value !== value) props.onChange(option.value);
    close();
  };

  const toggle = () => {
    if (open) return close();
    const i = options.findIndex((o) => o.value === value);
    setActive(Math.max(i, 0));
    setOpen(true);
  };

  // Позиция: под кнопкой, а не влезает вниз — над ней.
  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const r = button.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 300 && r.top > window.innerHeight - r.bottom;
    setPlace({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: r.width, up });
  }, [open]);

  // Клик мимо закрывает; прокрутка страницы — тоже (список висит в `body`).
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popup.current?.contains(t) && !button.current?.contains(t)) close(false);
    };
    const scroll = (e: Event) => {
      if (!popup.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", scroll);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", scroll);
    };
  }, [open]);

  // Активный пункт — в видимой части списка.
  useEffect(() => {
    list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") setActive((i) => Math.min(i + 1, shown.length - 1));
    else if (e.key === "ArrowUp") setActive((i) => Math.max(i - 1, 0));
    else if (e.key === "Enter") {
      const option = shown[active];
      if (option) pick(option);
    } else if (e.key === "Escape") close();
    else if (e.key === "Tab") close(false);
    else return;
    e.preventDefault();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        title={props.title ?? current?.label}
        onClick={toggle}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            toggle();
          }
        }}
        className={`${FIELD} ${SIZE[props.size ?? "md"]} ${
          open ? "border-border-strong" : props.warn ? "border-warn/60" : "border-border"
        } ${props.className ?? ""}`}
      >
        <span className={`min-w-0 flex-1 truncate ${current && current.value !== "" ? "" : "text-fg-muted"}`}>
          {current?.label ?? props.placeholder ?? "—"}
        </span>
        <ChevronDown size={13} className="flex-none text-fg-faint" />
      </button>
      {open && place
        ? createPortal(
            <div
              ref={popup}
              style={{
                left: place.left,
                minWidth: Math.max(place.width, 220),
                ...(place.up ? { bottom: window.innerHeight - place.top } : { top: place.top }),
              }}
              className="fixed z-50 max-w-[420px] overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated text-[12.5px] shadow-lg"
            >
              <div className="flex items-center gap-1.5 border-b border-border px-2">
                <Search size={13} className="flex-none text-fg-faint" />
                <input
                  autoFocus
                  className="h-8 w-full bg-transparent outline-none placeholder:text-fg-faint"
                  placeholder="поиск"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onKey}
                />
              </div>
              <div ref={list} className="max-h-[300px] overflow-y-auto py-1">
                {shown.length === 0 ? <div className="px-2.5 py-1.5 text-fg-faint">ничего не нашлось</div> : null}
                {shown.map((o, i) => (
                  <div key={`${o.group ?? ""}\u0000${o.value}`}>
                    {o.group && o.group !== shown[i - 1]?.group ? (
                      <div className="px-2.5 pt-2 pb-0.5 text-[10.5px] font-medium uppercase tracking-wide text-fg-faint">
                        {o.group}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      data-i={i}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => pick(o)}
                      className={`flex w-full items-center gap-1.5 py-1.5 pr-2.5 text-left ${o.group ? "pl-4" : "pl-2.5"} ${
                        i === active ? "bg-bg-hover" : ""
                      } ${o.value === value ? "text-accent-text" : "text-fg"}`}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.value === value ? <Check size={12} className="ml-auto flex-none" /> : null}
                    </button>
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Пункты из статей, сложенных по группам (`articleOptions`). */
export function groupOptions(groups: { group: string; items: { id: number; name: string }[] }[]): ComboOption[] {
  return groups.flatMap((g) => g.items.map((a) => ({ value: String(a.id), label: a.name, group: g.group })));
}
